import { el, clear } from "../components/dom.js";
import { canScanBarcodes, scanIsbn, isbnFrom } from "../components/isbn-scanner.js";
import { searchBar } from "../components/search-bar.js";
import { expandableResultCard } from "../components/search-result.js";
import { searchBooks, searchBookSeries, getBookByIsbn } from "../api/books.js";
import { addBook, deleteBookByOlId, listBooks } from "../db/books.js";
import { navigate, navigateBack } from "../main.js";
import { magScreenHead, MAG } from "../components/magazine/kit.js";
import { fannedSpines } from "../components/magazine/shelf.js";

// Search modes. "series" is special: instead of book results it searches book
// SERIES (Wikidata) and links each to the episode-style /books/series/:id view.
const SEARCH_FIELDS = [
	{ value: null, label: "All", placeholder: "Search books…" },
	{ value: "title", label: "Title", placeholder: "Search by title…" },
	{ value: "author", label: "Author", placeholder: "Search by author…" },
	{ value: "series", label: "Series", placeholder: "Find a series — e.g. Realm of the Elderlings…" },
	{ value: "publisher", label: "Publisher", placeholder: "Search by publisher…" },
];

export async function renderAddBook(root) {
	const head = magScreenHead("Add a Book", {
		eyebrow: "Find & collect",
		eyebrowColor: "#19d3e8",
		onBack: () => navigateBack("/books"),
	});

	const resultsContainer = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px", padding: "6px 0 24px" } });
	const statusLine = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9.5px",
			letterSpacing: ".14em", textTransform: "uppercase", color: MAG.lime, padding: "10px 0 4px",
		},
	});

	const existing = await listBooks();
	const addedIds = new Set(existing.map((b) => b.ol_id));

	let inflight = null;
	let lastResults = [];
	let lastSeries = []; // matching series, surfaced at the top of a normal search
	let currentField = null;

	function renderResults() {
		clear(resultsContainer);
		if (currentField === "series") {
			for (const s of lastResults) resultsContainer.append(seriesCard(s));
			return;
		}
		// In a normal (All / Title) search, matching series lead the results so a
		// query like "wheel of time" surfaces the whole series first, clearly
		// marked — tapping it opens the series (it's not added/owned by appearing).
		for (const s of lastSeries) resultsContainer.append(seriesCard(s));
		for (const result of lastResults) {
			const authors = (result.authors ?? []).join(", ");
			resultsContainer.append(
				expandableResultCard({
					cover_url: result.cover_url,
					mediaType: "book",
					title: result.title,
					subtitle: [authors, result.published_year].filter(Boolean).join(" · "),
					details: [
						{ label: "Author", value: authors },
						{ label: "Published", value: result.published_year ?? "" },
						{ label: "Subjects", value: (result.subjects ?? []).slice(0, 6).join(", ") },
					],
					added: addedIds.has(result.ol_id),
					onToggleAdd: (want) => toggle(result, want),
				}),
			);
		}
	}

	// A book-series result: fanned spines + Anton name + "author · N books",
	// navigating to the episode-style series view.
	function seriesCard(s) {
		const fakeMembers = Array.from({ length: Math.min(3, Math.max(1, s.book_count || 1)) });
		return el("div", {
			role: "button",
			onclick: () => navigate(`/books/series/${s.wd_id}`),
			style: {
				display: "flex", gap: "12px", alignItems: "center", padding: "12px", cursor: "pointer",
				borderRadius: "15px", background: "rgba(255,255,255,.06)", border: `1px solid ${MAG.line}`,
				backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)",
			},
		},
			fannedSpines(fakeMembers, { height: "56px", width: "26px" }),
			el("div", { style: { flex: "1", minWidth: "0" } },
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
						letterSpacing: ".12em", textTransform: "uppercase", color: "#0b0b0f",
						background: MAG.lime, display: "inline-block", padding: "2px 6px",
						transform: "skewX(-7deg)", marginBottom: "5px",
					},
				}, "◆ Series"),
				el("div", {
					style: { fontFamily: "'Anton', sans-serif", fontSize: "20px", lineHeight: ".95", textTransform: "uppercase", color: "#fff" },
				}, s.title),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9.5px",
						letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px",
					},
				}, [s.author, s.book_count ? `${s.book_count} books` : null].filter(Boolean).join(" · ") || "Book series"),
			),
			el("span", { style: { fontFamily: "'Anton', sans-serif", fontSize: "22px", color: MAG.lime } }, "›"),
		);
	}

	async function toggle(result, want) {
		try {
			if (!want) {
				await deleteBookByOlId(result.ol_id);
				addedIds.delete(result.ol_id);
				return false;
			}
			await addBook({
				ol_id: result.ol_id,
				title: result.title,
				authors: result.authors ?? [],
				published_year: result.published_year ?? null,
				cover_url: result.cover_url,
				subjects: result.subjects ?? [],
			});
			addedIds.add(result.ol_id);
			return true;
		} catch (err) {
			statusLine.textContent = `Could not ${want ? "add" : "remove"}: ${err.message}`;
			throw err;
		}
	}

	async function runSearch(q) {
		if (inflight) inflight.abort();
		lastResults = [];
		lastSeries = [];
		clear(resultsContainer);
		statusLine.textContent = "";
		if (!q) return;

		const controller = new AbortController();
		inflight = controller;
		statusLine.textContent = "Searching…";

		// An ISBN (typed or scanned) goes straight to its book (roadmap #42).
		const isbn = isbnFrom(q);
		if (isbn) {
			try {
				const book = await getBookByIsbn(isbn, { signal: controller.signal });
				if (controller.signal.aborted) return;
				lastResults = [book];
				statusLine.textContent = `ISBN ${isbn}`;
				renderResults();
			} catch (err) {
				if (err.name === "AbortError") return;
				statusLine.textContent = err.status === 404 ? `No book found for ISBN ${isbn}. Try the title instead.` : `Error: ${err.message}`;
			} finally {
				if (inflight === controller) inflight = null;
			}
			return;
		}

		// Series-only mode: just the series cards.
		if (currentField === "series") {
			try {
				const results = await searchBookSeries(q, { signal: controller.signal });
				if (controller.signal.aborted) return;
				lastResults = results;
				statusLine.textContent = results.length ? `${results.length} series` : "No results.";
				renderResults();
			} catch (err) {
				if (err.name !== "AbortError") statusLine.textContent = `Error: ${err.message}`;
			} finally {
				if (inflight === controller) inflight = null;
			}
			return;
		}

		// Normal search: also look up series (best-effort) and surface the top
		// match(es) above the book results — in EVERY mode, matched the way the
		// user means it. Author searches find series by their writer (e.g.
		// "Salvatore"); the rest match against series names.
		const seriesBy = currentField === "author" ? "author" : "name";
		const seriesP = searchBookSeries(q, { by: seriesBy, signal: controller.signal }).catch(() => []);

		try {
			const results = await searchBooks(q, { field: currentField, signal: controller.signal });
			if (controller.signal.aborted) return;
			lastResults = results;
			statusLine.textContent = results.length
				? `${results.length} result${results.length === 1 ? "" : "s"}`
				: "No results.";
			renderResults();
		} catch (err) {
			if (err.name === "AbortError") return;
			statusLine.textContent = `Error: ${err.message}`;
		} finally {
			if (inflight === controller) inflight = null;
		}

		if (seriesP) {
			const series = await seriesP;
			if (!controller.signal.aborted && Array.isArray(series) && series.length) {
				lastSeries = series.slice(0, 2);
				renderResults();
			}
		}
	}

	const bar = searchBar({
		placeholder: SEARCH_FIELDS[0].placeholder,
		onInput: (q) => runSearch(q),
	});

	// "Search by" pills — single-select. Changing the mode re-runs the query.
	const fieldChips = el("div", { style: { display: "flex", gap: "7px", flexWrap: "wrap", padding: "12px 0 0" } });
	function paintChips() {
		for (const c of fieldChips.children) {
			if (!c.dataset.value) continue; // the Scan button keeps its own look
			const on = c.dataset.value === String(currentField);
			Object.assign(c.style, {
				color: on ? "#0b0b0f" : MAG.dim, background: on ? MAG.lime : "rgba(255,255,255,.07)",
				border: on ? "0" : `1px solid ${MAG.line}`, fontWeight: on ? "900" : "800",
			});
		}
	}
	for (const f of SEARCH_FIELDS) {
		const chip = el("button", {
			type: "button",
			dataset: { value: String(f.value) },
			onclick: () => {
				if (currentField === f.value) return;
				currentField = f.value;
				bar.input.placeholder = f.placeholder;
				paintChips();
				runSearch(bar.input.value.trim());
			},
			style: {
				fontFamily: "'Archivo', sans-serif", fontSize: "10px", letterSpacing: ".05em",
				textTransform: "uppercase", borderRadius: "999px", padding: "7px 13px", cursor: "pointer",
			},
		}, f.label);
		fieldChips.append(chip);
	}
	paintChips();
	if (canScanBarcodes()) {
		fieldChips.append(el("button", {
			type: "button",
			onclick: async () => {
				try {
					const isbn = await scanIsbn();
					if (!isbn) return;
					bar.input.value = isbn;
					runSearch(isbn);
				} catch (err) {
					statusLine.textContent = err?.name === "NotAllowedError" ? "Camera permission is off for this site." : `Couldn't open the camera: ${err?.message ?? err}`;
				}
			},
			style: {
				fontFamily: "'Archivo', sans-serif", fontSize: "10px", letterSpacing: ".05em", fontWeight: "900",
				textTransform: "uppercase", borderRadius: "999px", padding: "7px 13px", cursor: "pointer",
				color: "#0b0b0f", background: "#ffe600", border: "0",
			},
		}, "📷 Scan ISBN"));
	}

	root.append(
		el("div.view", {},
			head,
			el("div", { style: { padding: "14px 0 0" } }, bar.node),
			fieldChips,
			statusLine,
			resultsContainer,
		),
	);
}
